import { add, setYear } from 'date-fns';
import { memo, useCallback, useMemo } from 'react';
import { View, type ViewStyle } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import DayNames from 'react-native-molecules/components/DatePickerInline/DayNames';
import { HeaderItem } from './HeaderItem';
import {
    useDatePickerInlineStoreRef,
    useDatePickerInlineStoreValue,
} from 'react-native-molecules/components/DatePickerInline/store';
import { datePickerHeaderStyles } from 'react-native-molecules/components/DatePickerInline';
import type { DatePickerLocale } from 'react-native-molecules/components/DatePicker';
import type { DisableWeekDaysType } from 'react-native-molecules/components/DatePickerInline/dateUtils';

export type DockedHeaderProps = {
    disableWeekDays?: DisableWeekDaysType;
    locale?: DatePickerLocale;
    scrollMode: 'horizontal' | 'vertical';
    style?: ViewStyle;
};

function DockedHeader({ disableWeekDays, locale = 'en', scrollMode, style: styleProp }: DockedHeaderProps) {
    const setStore = useDatePickerInlineStoreRef().set;

    const { localDate, pickerType } = useDatePickerInlineStoreValue((state) => ({
        localDate: state.localDate,
        pickerType: state.pickerType,
    }));

    const isHorizontal = scrollMode === 'horizontal';

    const { monthName, year } = useMemo(
        () => ({
            monthName: new Intl.DateTimeFormat(locale, { month: 'short' }).format(localDate),
            year: localDate.getFullYear(),
        }),
        [localDate, locale]
    );

    const handleMonthDropdown = useCallback(() => {
        setStore((prev) => ({ pickerType: prev.pickerType === 'month' ? undefined : 'month' }));
    }, [setStore]);

    const handleYearDropdown = useCallback(() => {
        setStore((prev) => ({ pickerType: prev.pickerType === 'year' ? undefined : 'year' }));
    }, [setStore]);

    const handleMonthPrev = useCallback(() => {
        setStore((prev) => ({ localDate: add(prev.localDate, { months: -1 }) }));
    }, [setStore]);

    const handleMonthNext = useCallback(() => {
        setStore((prev) => ({ localDate: add(prev.localDate, { months: 1 }) }));
    }, [setStore]);

    const handleYearPrev = useCallback(() => {
        setStore((prev) => ({
            localDate: setYear(prev.localDate, prev.localDate.getFullYear() - 1),
        }));
    }, [setStore]);

    const handleYearNext = useCallback(() => {
        setStore((prev) => ({
            localDate: setYear(prev.localDate, prev.localDate.getFullYear() + 1),
        }));
    }, [setStore]);

    return (
        <View pointerEvents="box-none">
            {isHorizontal && (
                <View style={[datePickerHeaderStyles.datePickerHeader, styles.row, styleProp]}>
                    <HeaderItem
                        onNext={handleMonthNext}
                        onPressDropdown={handleMonthDropdown}
                        onPrev={handleMonthPrev}
                        pickerType={pickerType}
                        selecting={pickerType === 'month'}
                        type="month"
                        value={monthName}
                    />
                    <HeaderItem
                        onNext={handleYearNext}
                        onPressDropdown={handleYearDropdown}
                        onPrev={handleYearPrev}
                        pickerType={pickerType}
                        selecting={pickerType === 'year'}
                        type="year"
                        value={year}
                    />
                </View>
            )}
            <DayNames disableWeekDays={disableWeekDays} locale={locale} />
        </View>
    );
}

const styles = StyleSheet.create({
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
});

export default memo(DockedHeader);
